// REVIEW CATEGORY CONFIG
export const REVIEW_CATEGORY_CONFIG = {
  tenant: [
    { key: "paysOnTime", label: "Pays on time" },
    { key: "cleanliness", label: "Cleanliness" },
    { key: "communication", label: "Communication" },
    { key: "respectsProperty", label: "Respects property" },
    { key: "followsRules", label: "Follows lease rules" },
  ],
  landlord: [
    { key: "maintenanceResponse", label: "Maintenance response" },
    { key: "communication", label: "Communication" },
    { key: "fairness", label: "Fairness" },
    { key: "professionalism", label: "Professionalism" },
    { key: "propertyCondition", label: "Property condition" },
  ],
  property_manager: [
    { key: "responsiveness", label: "Responsiveness" },
    { key: "maintenanceHandling", label: "Maintenance handling" },
    { key: "professionalism", label: "Professionalism" },
    { key: "organization", label: "Organization" },
    { key: "fairness", label: "Fairness" },
  ],
};

export const getReviewCategoriesForRole = (roleValue = "tenant") =>
  REVIEW_CATEGORY_CONFIG[roleValue] || REVIEW_CATEGORY_CONFIG.tenant;

export const buildInitialCategoryRatings = (roleValue = "tenant") =>
  getReviewCategoriesForRole(roleValue).reduce((acc, category) => {
    acc[category.key] = 0;
    return acc;
  }, {});

export const calculateCategoryOverall = (categoryRatings = {}) => {
  const values = Object.values(categoryRatings)
    .map((value) => Number(value || 0))
    .filter((value) => value > 0);

  if (!values.length) return 0;

  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
};

export const getCategoryLabel = (targetRole = "tenant", categoryKey = "") =>
  getReviewCategoriesForRole(targetRole).find((item) => item.key === categoryKey)?.label ||
  categoryKey;

export const formatTimeAgo = (timestamp) => {
  if (!timestamp?.seconds) return "";

  const date = new Date(timestamp.seconds * 1000);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)} wk ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} mo ago`;

  return `${Math.floor(diff / 31536000)} yr ago`;
};

export const formatReviewDateTime = (timestamp) => {
  if (!timestamp?.seconds) return "";

  const date = new Date(timestamp.seconds * 1000);
  const datePart = date.toLocaleDateString();
  const timePart = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return `${datePart} • ${timePart}`;
};

const normalizeForAI = (value = "") =>
  String(value).toLowerCase().replace(/\s+/g, " ").trim();

const tokenizeForAI = (value = "") =>
  normalizeForAI(value)
    .split(" ")
    .map((word) => word.replace(/[^a-z0-9]/gi, ""))
    .filter(Boolean);

export const getTextSimilarityScore = (a = "", b = "") => {
  const setA = new Set(tokenizeForAI(a));
  const setB = new Set(tokenizeForAI(b));

  if (!setA.size || !setB.size) return 0;

  let intersection = 0;
  setA.forEach((token) => {
    if (setB.has(token)) intersection += 1;
  });

  const union = new Set([...setA, ...setB]).size;
  return union ? intersection / union : 0;
};

export const getSimilarReviewMatches = ({
  existingReviews = [],
  reviewText = "",
  targetUserId = "",
  reviewerUserId = "",
}) => {
  return existingReviews
    .filter((item) => item?.text && item?.targetUserId === targetUserId)
    .filter((item) => item?.reviewerUserId !== reviewerUserId)
    .map((item) => ({
      reviewId: item.id,
      similarity: getTextSimilarityScore(reviewText, item.text),
      reviewerUserId: item.reviewerUserId || "",
      text: item.text || "",
    }))
    .filter((item) => item.similarity >= 0.72)
    .sort((a, b) => b.similarity - a.similarity);
};

export const getCrossAccountScamSignals = ({
  existingReviews = [],
  reviewText = "",
  reviewerUserId = "",
  targetUserId = "",
}) => {
  const normalizedText = normalizeForAI(reviewText);

  const sameTextOtherTargets = existingReviews.filter((item) => {
    const sameMeaning =
      getTextSimilarityScore(normalizedText, item?.text || "") >= 0.82;

    return (
      item?.reviewerUserId === reviewerUserId &&
      item?.targetUserId !== targetUserId &&
      sameMeaning
    );
  });

  const reportsAgainstTarget = existingReviews.filter(
    (item) => item?.targetUserId === targetUserId && item?.reported === true
  ).length;

  const repeatedExtremesByReviewer = existingReviews.filter((item) => {
    return (
      item?.reviewerUserId === reviewerUserId &&
      (Number(item?.rating || 0) === 1 || Number(item?.rating || 0) === 5)
    );
  }).length;

  return {
    sameTextOtherTargetsCount: sameTextOtherTargets.length,
    reportsAgainstTarget,
    repeatedExtremesByReviewer,
  };
};

export const buildProfessionalReplyText = (value = "", tone = "professional") => {
  const cleaned = String(value || "").replace(/\s+/g, " ").trim();

  if (!cleaned) return "";

  let sentence = cleaned
    .replace(/^./, (char) => char.toUpperCase());

  if (!/[.!?]$/.test(sentence)) sentence += ".";

  return `Thank you for the feedback. ${sentence} We appreciate the opportunity to respond professionally.`;
};

export const calculateRentScore = (items = []) => {
  const reviews = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!reviews.length) {
    return { score: 0, label: "No Data" };
  }

  const averageRating =
    reviews.reduce((sum, item) => sum + Number(item?.rating || 0), 0) / reviews.length;

  const score = Math.max(0, Math.min(100, Math.round(averageRating * 20)));

  let label = "High Risk";
  if (score >= 85) label = "Elite";
  else if (score >= 70) label = "Trusted";
  else if (score >= 55) label = "Watch";

  return { score, label };
};